/* eslint no-shadow: 0 */
import undoable, { excludeAction } from 'redux-undo';
import { AnyAction } from 'redux';
import produce from 'immer';

import * as canvasBlocksActionTypes from '../../constants/actionTypes/canvasBlocks';
import * as columnActionTypes from '../../constants/actionTypes/column';
import * as columnItemActionTypes from '../../constants/actionTypes/columnItem';
import * as selectedElementsActionTypes from '../../constants/actionTypes/selectedElements';
import * as selectedBlocksActionTypes from '../../constants/actionTypes/selectedBlocks';
import * as htmlOutputActionTypes from '../../constants/actionTypes/htmlOutput';
import * as versionHistoryActionTypes from '../../constants/actionTypes/versionHistory';
import * as browserActionTypes from '../../constants/actionTypes/browser';
import * as sessionActionTypes from '../../constants/actionTypes/session';
import * as canvasErrorActionTypes from '../../constants/actionTypes/elementErrors';
import * as modalActionTypes from '../../constants/actionTypes/modals';
import * as socketsActionTypes from '../../constants/actionTypes/sockets';
import * as savingActionTypes from '../../constants/actionTypes/saving';
import * as fontsActionTypes from '../../constants/actionTypes/customFonts';
import * as thirdPartyContent from '../../constants/actionTypes/thirdPartyContent';
import canvasBlockMethods from '../methods/canvasBlocks';
import columnMethods from '../methods/columns';
import columnItemsMethods from '../methods/columnItems';
import { IBlock } from '../../types/content';

export const canvasBlocks = (state: IBlock[] = [], action: AnyAction) =>
    produce(state, (draftState) => {
        switch (action.type) {
            case canvasBlocksActionTypes.POPULATE_INITIAL_BLOCKS:
                return canvasBlockMethods.populateInitialBlocks(draftState, action);

            case canvasBlocksActionTypes.SWITCH_LOCK_STRUCTURE:
                return canvasBlockMethods.switchLockStructure(draftState, action);

            case canvasBlocksActionTypes.ADD_BLOCK:
                return canvasBlockMethods.addBlock(draftState, action);

            case canvasBlocksActionTypes.DUPLICATE_BLOCKS:
                return canvasBlockMethods.duplicateBlocks(draftState, action);

            case canvasBlocksActionTypes.REMOVE_BLOCK:
                return canvasBlockMethods.removeBlock(draftState, action);

            case canvasBlocksActionTypes.MOVE_BLOCKS:
                return canvasBlockMethods.moveBlocks(draftState, action);

            case canvasBlocksActionTypes.SWAP_BLOCKS:
                return canvasBlockMethods.swapBlocks(draftState, action);

            case canvasBlocksActionTypes.DELETE_BLOCKS:
                return canvasBlockMethods.deleteBlocks(draftState, action);

            case canvasBlocksActionTypes.LOCK_BLOCKS:
                return canvasBlockMethods.lockBlocks(draftState, action);

            case canvasBlocksActionTypes.UPDATE_BLOCKS_PROPERTIES:
                return canvasBlockMethods.updateBlocksProperties(draftState, action);

            /* COLUMN */
            case columnActionTypes.MERGE_COLUMN:
                return columnMethods.mergeColumn(draftState, action);

            case columnActionTypes.MOVE_COLUMN:
                return columnMethods.moveColumn(draftState, action);

            case columnActionTypes.SPLIT_COLUMN:
                return columnMethods.splitColumn(draftState, action);

            case columnActionTypes.UPDATE_COLUMN_SIZING:
                return columnMethods.updateColumnSizing(draftState, action);

            /* COLUMN ITEM */
            case columnItemActionTypes.UPDATE_COLUMN_ITEM_CONTENT:
            case columnItemActionTypes.REFRESH_COLUMN_ITEM_CONTENT:
                return columnItemsMethods.updateColumnItemContent(draftState, action);

            case columnItemActionTypes.MOVE_COLUMN_ITEM_CONTENT_TO_ANOTHER_COLUMN:
                return columnItemsMethods.moveColumnItemContentToAnotherColumn(draftState, action);

            case columnItemActionTypes.DUPLICATE_COLUMN_ITEM:
                return columnItemsMethods.duplicateColumnItem(draftState, action);

            case columnItemActionTypes.DELETE_COLUMN_ITEM:
                return columnItemsMethods.deleteColumnItem(draftState, action);

            case columnItemActionTypes.ADD_NEW_COLUMN_ITEM:
                return columnItemsMethods.addNewColumnItem(draftState, action);

            case columnItemActionTypes.UPDATE_COLUMN_ITEM_CONTENT_IN_MULTIPLE_BLOCKS:
                return columnItemsMethods.updateColumnItemContentInMultipleBlock(draftState, action);

            case columnItemActionTypes.UPDATE_COLUMN_ITEM_FONT_IN_MULTIPLE_BLOCKS:
                return columnItemsMethods.updateColumnItemFontInMultipleBlock(draftState, action);

            default:
                return state;
        }
    });

export default undoable(canvasBlocks, {
    filter: excludeAction([
        columnItemActionTypes.REFRESH_COLUMN_ITEM_CONTENT,
        htmlOutputActionTypes.UPDATE_HTML_BODY,
        htmlOutputActionTypes.UPDATE_HTML_BODY_LOADED_STATUS,
        versionHistoryActionTypes.UPDATE_VERSION_HISTORY,
        versionHistoryActionTypes.CLEAR_VERSION_HISTORY,
        versionHistoryActionTypes.UPDATE_VERSION_HISTORY_ITEM,
        versionHistoryActionTypes.UPDATE_VERSION_HISTORY_ITEM_LOADED_STATUS,
        versionHistoryActionTypes.UPDATE_VERSION_HISTORY_ITEM_SAVING_STATUS,
        versionHistoryActionTypes.UPDATE_VERSION_HISTORY_LATEST_SAVED_ITEM,
        selectedElementsActionTypes.ADD_SELECTED_ELEMENT,
        selectedElementsActionTypes.REMOVE_SELECTED_ELEMENT,
        selectedElementsActionTypes.REMOVE_ALL_SELECTED_ELEMENTS,
        selectedBlocksActionTypes.ADD_SELECTED_BLOCK,
        selectedBlocksActionTypes.REMOVE_SELECTED_BLOCK,
        selectedBlocksActionTypes.SELECT_ALL_BLOCKS,
        selectedBlocksActionTypes.REMOVE_ALL_SELECTED_BLOCKS,
        browserActionTypes.UPDATE_MODIFIER_KEY_PRESSED_STATUS,
        browserActionTypes.UPDATE_LEFT_PANEL_COLLAPSED_STATUS,
        browserActionTypes.SET_BROWSER_NAME,
        browserActionTypes.SET_OS_NAME,
        sessionActionTypes.SET_SESSION,
        sessionActionTypes.SET_LOADING,
        sessionActionTypes.UPDATE_AUTHORIZATION,
        canvasErrorActionTypes.ADD_ERRONEOUS_ELEMENT,
        canvasErrorActionTypes.DELETE_ERRONEOUS_ELEMENT,
        modalActionTypes.SET_MODAL,
        socketsActionTypes.UPDATE_CONNECTED_CLIENTS,
        socketsActionTypes.SEND_UNSAVED_ACTIONS,
        canvasBlocksActionTypes.POPULATE_INITIAL_BLOCKS,
        savingActionTypes.UPDATE_SAVING_STATUS,
        fontsActionTypes.SET_LOADED_STATUS,
        fontsActionTypes.UPDATE_CUSTOM_FONTS,
        thirdPartyContent.CONFIG.FETCH_BEGIN,
        thirdPartyContent.CONFIG.FETCH_SUCCESS,
        thirdPartyContent.CONFIG.FETCH_ERROR,
        thirdPartyContent.POSTS.FETCH_BEGIN,
        thirdPartyContent.POSTS.FETCH_SUCCESS,
        thirdPartyContent.POSTS.FETCH_ERROR,
        thirdPartyContent.POSTS.RESET_POSTS,
        thirdPartyContent.SET_BLOCK_POPULATING,
    ]),
    ignoreInitialState: true,
});
